import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { useSaveMyUserSignedMutation } from '../../query/hooks/useMyUserSigned';
import { SignatureCapture, type SignatureCaptureHandle } from './SignatureCapture';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function DefaultSignatureDialog({ open, onClose }: Props) {
  const { jwt } = useAuth();
  const padRef = useRef<SignatureCaptureHandle | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveM = useSaveMyUserSignedMutation(jwt);

  useEffect(() => {
    if (!open) {
      setReady(false);
      setError(null);
    }
  }, [open]);

  const busy = saveM.isPending;

  async function handleSave() {
    const dataUrl = padRef.current?.exportPng()?.trim() ?? '';
    if (!dataUrl) return;
    setError(null);
    try {
      await saveM.mutateAsync(dataUrl);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan tanda tangan default.');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="default-signature-dialog-title"
      data-testid="default-signature-dialog"
    >
      <DialogTitle id="default-signature-dialog-title" sx={{ pr: 6 }}>
        Simpan tanda tangan default
        <IconButton
          aria-label="Tutup"
          onClick={onClose}
          disabled={busy}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tanda tangan ini dapat dipakai ulang pada form yang membutuhkan tanda tangan.
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <SignatureCapture
          ref={padRef}
          open={open}
          disabled={busy}
          allowDefault={false}
          canvasTestId="default-signature-canvas"
          onReadyChange={setReady}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Batal
        </Button>
        <Button
          variant="contained"
          disabled={!ready || busy || !jwt}
          onClick={() => void handleSave()}
          data-testid="default-signature-save"
        >
          {busy ? 'Menyimpan…' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
